import { useEffect, useState } from 'react';
import { CodeBlock, Spinner, Tabs, TabsPanel, Text } from '@customerio/pluma-components/react';
import { HtmlPreview } from '@/components/html-preview';
import { isEmptyString } from '@/utils/string';
import { inAppEncodedMessageHtml } from '../utils/in-app-config';
import { decodeGzipBase64 } from '../utils/decode-message-html';
import { formatBodyJson } from '../utils/message-json';

const IN_APP_TABS = [
  { value: 'message', text: 'Message' },
  { value: 'json', text: 'JSON' },
];

type DecodeStatus = 'idle' | 'loading' | 'ready' | 'error';
type DecodedInAppHtml = { html: string | undefined; status: DecodeStatus };

/**
 * Decodes the visual-editor message HTML via `decodeGzipBase64` (a
 * `DecompressionStream` pipeline — the external system this effect
 * synchronizes with). A single `cancelled` flag drops the result of a
 * superseded or unmounted decode. A decode failure resolves to
 * `status: 'error'` rather than throwing, so the tab stays put — matching
 * Ember's snackbar-and-empty behavior.
 */
function useDecodedInAppHtml(encoded: string | undefined): DecodedInAppHtml {
  const [state, setState] = useState<DecodedInAppHtml>({ html: undefined, status: 'idle' });

  useEffect(() => {
    if (isEmptyString(encoded)) {
      setState({ html: undefined, status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ html: undefined, status: 'loading' });
    void decodeGzipBase64(encoded)
      .then((html) => {
        if (!cancelled) setState({ html, status: 'ready' });
      })
      .catch(() => {
        if (!cancelled) setState({ html: undefined, status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [encoded]);

  return state;
}

type InAppVisualMessageProps = { encoded: string | undefined };

/**
 * The Message tab body. A visual-editor message carries `encodedMessageHtml`,
 * which we decode and render in the sandboxed `HtmlPreview`. A JSON-editor
 * message has no encoded HTML; Ember rebuilds its visual from `body_json`
 * through the composer, which is deferred here — we point the user to the JSON
 * tab instead.
 */
function InAppVisualMessage({ encoded }: InAppVisualMessageProps) {
  const { html, status } = useDecodedInAppHtml(encoded);

  if (isEmptyString(encoded)) {
    return (
      <Text size="s" color="subtle" data-test-in-app-visual-unavailable>
        A visual preview isn&apos;t available for this message. See the JSON tab for the full payload.
      </Text>
    );
  }

  switch (status) {
    case 'idle':
    case 'loading':
      return <Spinner data-test-in-app-visual-loading />;
    case 'error':
      return (
        <Text size="s" color="subtle" data-test-in-app-visual-error>
          We weren&apos;t able to load the message content.
        </Text>
      );
    case 'ready':
      return <HtmlPreview body={html ?? ''} className="fs-mask" />;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

type InAppMessagePreviewProps = { body: string };

/**
 * In-app delivery preview — a Message tab (default) and a JSON tab, mirroring
 * Ember's two-tab preview. The Message tab renders the decoded visual-editor
 * HTML; the JSON tab shows the full gist payload.
 */
export function InAppMessagePreview({ body }: InAppMessagePreviewProps) {
  const encodedMessageHtml = inAppEncodedMessageHtml(body);

  return (
    <Tabs tabs={IN_APP_TABS} isBorderless data-test-delivery-preview-in-app>
      <TabsPanel value="message" p="200" data-test-in-app-message-tab>
        <InAppVisualMessage encoded={encodedMessageHtml} />
      </TabsPanel>
      <TabsPanel value="json" p="200" data-test-in-app-json-tab>
        <CodeBlock className="fs-mask" code={formatBodyJson(body)} language="json" withLineNumbers />
      </TabsPanel>
    </Tabs>
  );
}
